import {
  ButtonType,
  CovButton,
  CovDialog,
  CovIcon,
  IconName,
} from "@cx-component-library/react-components";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Box, IconButton, Typography } from "@mui/material";
import type { WhatsNewCtaTarget, WhatsNewStep } from "models/whats-new";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./WhatsNewDialog.css";
import { WhatsNewMedia } from "./WhatsNewMedia";

interface WhatsNewDialogProps {
  open: boolean;
  steps: WhatsNewStep[];
  title?: string;
  onClose: () => void;
  onExplore: (target: WhatsNewCtaTarget) => void;
}

/**
 * Multi-step "What's New" release popup.
 *
 * @see Figma: nodes 3748-10775 (step 1) and 3748-11805 (step 2) in Imagio Color Finder 2.0
 * @see Ticket: CX-3596
 */
export const WhatsNewDialog = ({
  open,
  steps,
  title,
  onClose,
  onExplore,
}: WhatsNewDialogProps) => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (open) setActiveStep(0);
  }, [open]);

  if (steps.length === 0) return null;

  const isFirst = activeStep === 0;
  const isLast = activeStep === steps.length - 1;
  const step = steps[activeStep];

  const handleExplore = () => {
    if (step.cta) {
      onExplore(step.cta.target);
    } else {
      onClose();
    }
  };

  return (
    <CovDialog
      className="whats-new-dialog"
      data-cy="WhatsNewDialog"
      open={open}
      onClose={onClose}
      title={title}
      dialogContent={
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--spacing16, 16px)",
          }}
        >
          {steps.length > 1 && (
            <Box
              sx={{
                display: "flex",
                gap: "var(--spacing4, 4px)",
                width: "100%",
              }}
            >
              {steps.map((s, index) => (
                <Box
                  key={s.id}
                  sx={{
                    flex: 1,
                    height: "3px",
                    borderRadius: "var(--radius2, 2px)",
                    backgroundColor:
                      index <= activeStep
                        ? "var(--alias-text-on-white-primary, #1a1a1a)"
                        : "var(--alias-border-primary, #e0e0e0)",
                  }}
                />
              ))}
            </Box>
          )}
          <WhatsNewMedia media={step.media} />
          <Typography
            sx={{
              fontFamily: 'var(--body-b2-14-regular-font, "AktivGrotesk")',
              fontWeight: "var(--body-b2-14-regular-weight, 400)",
              fontSize: "var(--body-b2-14-regular-size, 14px)",
              lineHeight: "var(--body-b2-14-regular-line-height, 20px)",
              color: "var(--alias-text-on-white-secondary, #6b6b6b)",
            }}
          >
            {step.description}
          </Typography>
        </Box>
      }
      actions={
        <>
          {!isFirst && (
            <IconButton
              className="whats-new-back"
              aria-label={t("whatsNew.back")}
              onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
          <CovButton
            buttonType={ButtonType.SECONDARY_OUTLINE}
            onClick={onClose}
          >
            {t("whatsNew.close")}
          </CovButton>
          {isLast ? (
            <CovButton buttonType={ButtonType.PRIMARY} onClick={handleExplore}>
              {step.cta?.label ?? t("whatsNew.explore")}
            </CovButton>
          ) : (
            <CovButton
              buttonType={ButtonType.PRIMARY}
              contentRight={
                <CovIcon
                  iconName={IconName.CHEVRON_RIGHT}
                  iconSize="sm"
                  ariaHidden
                />
              }
              onClick={() =>
                setActiveStep((s) => Math.min(steps.length - 1, s + 1))
              }
            >
              {t("whatsNew.next")}
            </CovButton>
          )}
        </>
      }
    />
  );
};
